import { Globe, Users } from 'lucide-react'
import type { Contact } from '@/types'

interface ContatosTableProps {
  contatos: Contact[]
  loading?: boolean
  onSelectContato?: (contato: Contact) => void
  onEdit?: (contato: Contact) => void
  onDelete?: (contato: Contact) => void
}

export function ContatosTable({ contatos, loading, onSelectContato, onEdit, onDelete }: ContatosTableProps) {
  if (loading) {
    return (
      <div className='glass-card p-8 text-center'>
        <p className='text-[var(--font-color-muted)] text-sm'>Carregando contatos...</p>
      </div>
    )
  }

  if (contatos.length === 0) {
    return (
      <div className='glass-card p-12 text-center'>
        <Users size={48} className='mx-auto mb-3 text-[var(--text-muted)] opacity-50' />
        <p className='text-[var(--font-color-body)] font-medium'>Nenhum contato encontrado</p>
        <p className='text-[var(--font-color-muted)] text-sm mt-1'>Adicione contatos a esta empresa</p>
      </div>
    )
  }

  return (
    <div className='glass-card p-0 overflow-hidden'>
      <div className='overflow-x-auto'>
        <table className='w-full'>
          <thead>
            <tr className='border-b border-[var(--glass-border)]'>
              {['Nome', 'Email', 'Telefone', 'Cargo', 'Decisor', 'Ações'].map((h) => (
                <th
                  key={h}
                  className='text-left px-4 py-3 text-[var(--font-color-label)] font-medium uppercase tracking-wider'
                  style={{ fontSize: 'var(--font-size-label)' }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {contatos.map((contato) => (
              <tr
                key={contato.id}
                className='border-b border-[var(--glass-border)] last:border-0 hover:bg-[var(--glass-bg)] transition-colors cursor-pointer'
                onClick={() => onSelectContato?.(contato)}
              >
                <td className='px-4 py-3'>
                  <div className='flex items-center gap-3'>
                    <div className='w-8 h-8 rounded-lg bg-[var(--brand-primary-muted)] flex items-center justify-center shrink-0'>
                      {contato.fotoUrl ? (
                        <img src={contato.fotoUrl} alt='' className='w-full h-full rounded-lg object-cover' />
                      ) : (
                        <span className='text-[11px] font-bold text-[var(--brand-primary)]'>
                          {(contato.nome || '??').split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)}
                        </span>
                      )}
                    </div>
                    <span
                      className='font-medium text-[var(--font-color-body)]'
                      style={{ fontSize: 'var(--font-size-sm)' }}
                    >
                      {contato.nome}
                    </span>
                  </div>
                </td>
                <td className='px-4 py-3'>
                  <span
                    className='text-[var(--font-color-muted)]'
                    style={{ fontSize: 'var(--font-size-sm)' }}
                  >
                    {contato.email ?? '—'}
                  </span>
                </td>
                <td className='px-4 py-3'>
                  <span
                    className='text-[var(--font-color-muted)]'
                    style={{ fontSize: 'var(--font-size-sm)' }}
                  >
                    {contato.telefone ?? '—'}
                  </span>
                </td>
                <td className='px-4 py-3'>
                  <span
                    className='text-[var(--font-color-muted)]'
                    style={{ fontSize: 'var(--font-size-sm)' }}
                  >
                    {contato.cargo ?? '—'}
                  </span>
                </td>
                <td className='px-4 py-3'>
                  {contato.decisor ? (
                    <span className='px-2 py-0.5 rounded-md text-[9px] font-black uppercase bg-indigo-500/10 text-indigo-400 border border-indigo-500/20'>
                      Decisor
                    </span>
                  ) : (
                    <span className='px-2 py-0.5 rounded-md text-[9px] font-bold uppercase bg-[var(--glass-bg)] text-[var(--text-muted)] border border-[var(--glass-border)]'>
                      Influenciador
                    </span>
                  )}
                </td>
                <td className='px-4 py-3'>
                  <div className='flex items-center gap-1'>
                    {contato.linkedin && (
                      <a
                        href={contato.linkedin}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='p-1.5 rounded-md text-[var(--text-muted)] hover:text-[var(--brand-primary)] hover:bg-[var(--glass-bg)] transition-colors'
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Globe size={13} />
                      </a>
                    )}
                    {onEdit && (
                      <button
                        className='p-1.5 rounded-md text-[var(--text-muted)] hover:text-[var(--brand-primary)] hover:bg-[var(--glass-bg)] transition-colors'
                        onClick={(e) => { e.stopPropagation(); onEdit(contato) }}
                      >
                        Editar
                      </button>
                    )}
                    {onDelete && (
                      <button
                        className='p-1.5 rounded-md text-[var(--text-muted)] hover:text-[#e5484d] hover:bg-[var(--glass-bg)] transition-colors'
                        onClick={(e) => { e.stopPropagation(); onDelete(contato) }}
                      >
                        Excluir
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
